import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    buildParticipantAttributeGroups,
    indexParticipantAttributes,
    narrowTeamsTo,
} from '../participantAttributes';

import { useGetCompanyInformation } from '@/api/directory';
import { useGetChoosableTeamManagerIds } from '@/api/objectives';

/**
 * The company's attribute values a team objective may take its participants from. `groups` and
 * `attributesByKey` name every value, chosen or not, and stay undefined until the company has
 * answered, so a value is only ever called missing from a company actually read; `isError` tells a
 * failed read from one still on its way.
 *
 * `choosableGroups` are the values the reader may add: under `teamsWithinReach`, "Team of" is
 * narrowed to the teams the server lets them choose, and the groups wait for that answer too.
 */
export const useParticipantAttributeGroups = ({
    enabled,
    teamsWithinReach = false,
}: {
    enabled: boolean;
    teamsWithinReach?: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const { data: company, isError: isCompanyError } = useGetCompanyInformation({ enabled });
    const { data: teamManagerIds, isError: isTeamsError } = useGetChoosableTeamManagerIds({
        enabled: teamsWithinReach,
    });

    // Rebuilt when the company or the language changes; `t` is a new function on every render.
    const indexed = useMemo(() => {
        if (!company) {
            return undefined;
        }

        const groups = buildParticipantAttributeGroups(company, t);
        return { groups, attributesByKey: indexParticipantAttributes(groups) };
    }, [company, i18n.language]);

    const choosableGroups = useMemo(() => {
        if (!teamsWithinReach) {
            return indexed?.groups;
        }

        return indexed && teamManagerIds
            ? narrowTeamsTo(indexed.groups, new Set(teamManagerIds))
            : undefined;
    }, [indexed, teamsWithinReach, teamManagerIds]);

    return {
        groups: indexed?.groups,
        attributesByKey: indexed?.attributesByKey,
        choosableGroups,
        isError: isCompanyError,
        isChoosableError: isCompanyError || (teamsWithinReach && isTeamsError),
    };
};
